import React from "react";
import { Button } from "../../../designSystem/button/Button";
import styled from "styled-components";
import { Tabber } from "../../../designSystem/Tabber";
import { rng, SurveyTopic } from '../../../amorg-common';
import { RadialPctChart } from "../../../designSystem/graphs/RadialPctChart";
import { AnswerRatioBarGraph } from "../../../features/surveys/report/AnswerRatioBarGraph";
import { Flex } from "../../../designSystem/compositeComponents/FlexHelpers";
import { ListRow } from "../../../designSystem/compositeComponents/ListRow";
import { ButtonRow } from "../../../designSystem/button/ButtonRow";
import { CloseModalButton } from "../../../designSystem/compositeComponents/CloseModalButton";
import { Modal } from "../../../designSystem/Modal";
import { createActionPlanTopic, getActionPlanSurveyTopics, updateActionPlanTopicData } from "../../../api/actionPlanClientApi";
import { LoadingPageOverlay } from "../../../designSystem/LoadingPageOverlay";



type SurveyTopicListButtonProps = {
    actionPlanId:string,
    onUpdated: () => void
}
export function SurveyTopicListButton (props: SurveyTopicListButtonProps): JSX.Element
{
    const [surveyTopics, setSurveyTopics] = React.useState<SurveyTopic[]>([]);
    const [showSurveyTopics, setShowSurveyTopics] = React.useState(false);
    const [loading, setLoading] = React.useState(false);

    React.useEffect(() =>
    {
        async function loadSurveyTopics()
        {
            setLoading(true);
            setSurveyTopics(await getActionPlanSurveyTopics(props.actionPlanId));
            setLoading(false);
        }
        loadSurveyTopics();
    }, [props.actionPlanId]);

    async function handleClickCreateSurveyTopic (st: SurveyTopic)
        {
            const topicId = rng.getRandomId();
            setLoading(true);
            if ((await createActionPlanTopic(topicId, props.actionPlanId)).isSuccess)
            {
                if (((await updateActionPlanTopicData(topicId, { type: 'WPA', customData: { surveyTopicIdentifier: st } })).isSuccess))
                {
                    setShowSurveyTopics(false);
                    props.onUpdated();
                }
            }
            setLoading(false);
        }

    return (
        <>
            <LoadingPageOverlay loading={loading} />
            
            <ButtonRow variant='stretched'>
                <Button
                    variant="light"
                    size='magnum'
                    onClick={() => setShowSurveyTopics(true)}
                    text={"Vælg emne fra rapporten"}
                    disabled={surveyTopics.length === 0} />
            </ButtonRow>
        
            {surveyTopics.length > 0 &&
                <Modal
                    title={"Emner fra spørgeskemaet"}
                    content={
                        <Flex flexDirection='column' gap={16}>
                            <ActionPlanSurveyTopicList
                                topics={surveyTopics}
                                onClickCreateTopic={handleClickCreateSurveyTopic} />
                            <CloseModalButton onClose={() => setShowSurveyTopics(false)} />
                        </Flex>
                    }
                    showModal={showSurveyTopics}
                    onCancel={() => setShowSurveyTopics(false)}
                />
            }
        </>
    );
}

type ActionPlanSurveyTopicListProps = {
    topics: SurveyTopic[],
    onClickCreateTopic: (st: SurveyTopic) => void
}
type TopicMode = 'theme' | 'indicator' | 'question'

function ActionPlanSurveyTopicList(props: ActionPlanSurveyTopicListProps): JSX.Element {
    const [mode, setMode] = React.useState<TopicMode>('theme');

    const selectedTopics = props.topics.filter(t => t.linkType === mode)
        .sort((a, b) => a.data.selections.selectionAverage - b.data.selections.selectionAverage);

    return (
        <>
            <div style={{margin:'10px'}}>
                <Tabber items={[
                        {tabName:'question', text:'Spørgsmål'},
                        {tabName:'theme', text:'Temaer'},
                        {tabName:'indicator', text:'Indikatorer'}
                    ]}
                    selectedTabName={mode}
                    onClick={(tabName) => setMode(tabName as TopicMode)} 
                />
            </div>
            <div style={{height:'60vh', overflowY:'scroll'}}>
                {selectedTopics.map((st, index) => {
                    return (
                        <ListRow key={index} styleVariant="neutral">
                            <TopicContainer>
                                <div>
                                    <RadialPctChart pct={st.data.selections.agreePlusPct} dim="60px" fontSize="0.6em" />
                                </div>
                                    
                                <div>
                                    <Flex flexDirection="column" gap={4}>
                                        <span>{st.data.title}</span>
                                        <AnswerRatioBarGraph data={st.data.selections.data} legend="none" showValueText />
                                    </Flex>
                                </div>
                                    
                                <Button variant="text"
                                    onClick={() => props.onClickCreateTopic(st)}
                                    icon="actions_plus"
                                    text="Vælg" />
                            </TopicContainer>
                        </ListRow>
                    );
                })}
            </div>
        </>
    );
}

const TopicContainer = styled.div`
    display:flex;
    margin:3px;
    padding:3px;
    align-items:center;
    justify-content: stretch;
    gap: 10px;
    > div:first-child{
        flex:0;
    }
    > div:nth-child(2) {
        flex:1;
        font-size: 12px;
    }
    > div:nth-child(3) {
        flex:0;
        width:120px;
    }
`;